import type { FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import type { FormikHelpers } from 'formik/dist/types'
import React from 'react'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import { Button, Fieldset, Input, ModalBox, Select } from '@fourthwall/components'
import {
  CHECKOUT_NON_SHIPPABLE_COUNTRIES,
  STATE_OPTIONS,
  getCountriesOptions,
} from '@fourthwall/utils/dist/inputs'
import { isRequired } from '@fourthwall/utils/dist/validation'

import { useOrderIdParam } from '../../hooks/useOrderIdParam'
import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import type { FormValues } from './OrderShippingAddressModal.types'
import { useUpdateShippingAddressMutation } from './queries/useUpdateShippingAddressMutation'

type OrderShippingAddressModalProps = {
  close: () => void
}

const countriesOptions = getCountriesOptions({
  exclude: CHECKOUT_NON_SHIPPABLE_COUNTRIES,
})

export const OrderShippingAddressModal = ({ close }: OrderShippingAddressModalProps) => {
  const { orderId } = useOrderIdParam()
  const { orderGroupedByShipmentsQuery, invalidateOrderGroupedByShipmentsQuery } =
    useOrderGroupedByShipmentsQuery([{ orderId }])
  const { updateShippingAddressMutation } = useUpdateShippingAddressMutation({
    onSuccess: () => {
      invalidateOrderGroupedByShipmentsQuery()
      close()
    },
  })
  const initialValues: FormValues = {
    firstName: orderGroupedByShipmentsQuery.data?.shipping.address?.firstName || '',
    lastName: orderGroupedByShipmentsQuery.data?.shipping.address?.lastName || '',
    address1: orderGroupedByShipmentsQuery.data?.shipping.address?.address1 || '',
    address2: orderGroupedByShipmentsQuery.data?.shipping.address?.address2 || '',
    country: orderGroupedByShipmentsQuery.data?.shipping.address?.country || 'US',
    state: orderGroupedByShipmentsQuery.data?.shipping.address?.state || '',
    zip: orderGroupedByShipmentsQuery.data?.shipping.address?.zip || '',
    city: orderGroupedByShipmentsQuery.data?.shipping.address?.city || '',
    phone: orderGroupedByShipmentsQuery.data?.shipping.address?.phone || '',
    vatId: orderGroupedByShipmentsQuery.data?.shipping.address?.vatId || '',
  }

  const isShippingToUS = (values: any) => values?.country === 'US'

  const getStateOptions = (values: any) => {
    return STATE_OPTIONS[values?.country]
  }

  const shouldDisplayStateDropdown = (values: any) => !!getStateOptions(values)

  const handleCountryChange = (setFieldValue: any, setFieldTouched: any) => {
    setFieldValue('state', '')
    setFieldTouched('state', false)
  }

  const handleSubmit = (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    resetForm({ values })

    updateShippingAddressMutation.mutate([{ orderId }, { address: values }])
  }

  if (!orderGroupedByShipmentsQuery.data?.shipping.address) {
    return null
  }

  const renderState = (values: FormValues) => {
    const isStateRequired = (value: string) => {
      if (getStateOptions(values)) {
        return isRequired('This field is required.')(value)
      }

      return undefined
    }

    if (shouldDisplayStateDropdown(values)) {
      return (
        <Field
          name="state"
          component={Select}
          validate={isStateRequired}
          label="State"
          options={getStateOptions(values)}
          fluid
        />
      )
    }

    return (
      <Field
        name="state"
        component={Input}
        validate={isStateRequired}
        label="State / Province"
        fluid
      />
    )
  }

  const renderVatId = (values: FormValues) => {
    if (values?.country === 'BR') {
      return (
        <Fieldset.Item>
          <Field
            name="vatId"
            component={Input}
            validate={isRequired('This field is required.')}
            label="CPF number"
            fluid
          />
        </Fieldset.Item>
      )
    }

    if (values?.country === 'MX') {
      return (
        <Fieldset.Item>
          <Field
            name="vatId"
            component={Input}
            validate={isRequired('This field is required.')}
            label="Tax ID (CURP or RFC)"
            fluid
          />
        </Fieldset.Item>
      )
    }

    return <Fieldset.Item />
  }

  const renderNames = (values: FormValues) => {
    // NOTE: handle old orders that are using both first and last name
    if (values?.firstName?.length && values?.lastName?.length) {
      return (
        <>
          <Fieldset.Item>
            <Field
              name="firstName"
              component={Input}
              validate={isRequired('This field is required.')}
              label="First Name"
              fluid
            />
          </Fieldset.Item>
          <Fieldset.Item>
            <Field
              name="lastName"
              component={Input}
              validate={isRequired('This field is required.')}
              label="Last Name"
              fluid
            />
          </Fieldset.Item>
        </>
      )
    }

    // NOTE: new orders use only firstName as fullName
    return (
      <Fieldset.Item>
        <Field
          name="firstName"
          component={Input}
          validate={isRequired('This field is required.')}
          label="Full Name"
          fluid
        />
      </Fieldset.Item>
    )
  }

  const renderForm = ({
    dirty,
    values,
    setFieldValue,
    setFieldTouched,
  }: FormikProps<FormValues>) => {
    return (
      <Form>
        <ModalBox
          title="Edit shipping details"
          onClose={close}
          buttons={[
            <Button
              label="Cancel"
              disabled={updateShippingAddressMutation.isLoading}
              onClick={close}
            />,
            <Button
              type="submit"
              label="Update shipping details"
              appearance="primary"
              loading={updateShippingAddressMutation.isLoading}
            />,
          ]}
        >
          <LeavingGuard when={dirty} />

          <Fieldset.Row>{renderNames(values)}</Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                name="address1"
                component={Input}
                validate={isRequired('This field is required.')}
                label="Street Address"
                fluid
              />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field name="address2" component={Input} label="Street Address 2" fluid />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item flex={2}>
              <Field
                name="country"
                component={Select}
                validate={isRequired('This field is required.')}
                label="Country"
                options={countriesOptions}
                fluid
                onValueChange={() => handleCountryChange(setFieldValue, setFieldTouched)}
              />
            </Fieldset.Item>
            <Fieldset.Item>
              <Field name="zip" component={Input} label="Zip Code" maskType="zipCode" fluid />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                name="city"
                component={Input}
                validate={isRequired('This field is required.')}
                label="City"
                fluid
              />
            </Fieldset.Item>
            <Fieldset.Item>{renderState(values)}</Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                name="phone"
                component={Input}
                validate={
                  isShippingToUS(values) ? undefined : isRequired('This field is required.')
                }
                label="Phone number"
                type="tel"
                fluid
              />
            </Fieldset.Item>
            {renderVatId(values)}
          </Fieldset.Row>
        </ModalBox>
      </Form>
    )
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      isInitialValid
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {renderForm}
    </Formik>
  )
}
